import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook to handle browser back/forward button navigation
 * Shows a modal warning when user tries to navigate using browser buttons
 * @param {boolean} enabled - Whether the warning should be active
 * @param {function} onNavigateAway - Optional callback when user confirms navigation
 */
export const useNavigationWarning = (enabled = true, onNavigateAway) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    let isHandlingPopState = false;

    const handlePopState = (event) => {
      // Prevent multiple triggers
      if (isHandlingPopState) return;
      isHandlingPopState = true;

      // Prevent the default browser back navigation
      event.preventDefault();
      
      // Push current state back to maintain position
      window.history.pushState(null, '', location.pathname);
      
      // Show modal instead of browser confirm
      setShowModal(true);
      
      // Reset flag after a short delay
      setTimeout(() => {
        isHandlingPopState = false;
      }, 100);
    };

    // Push initial state
    window.history.pushState(null, '', location.pathname);
    
    // Add event listener
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [enabled, location.pathname]);

  // Handle modal actions
  const handleConfirmNavigation = () => {
    setShowModal(false);
    
    // Call cleanup callback if provided
    if (onNavigateAway) {
      onNavigateAway();
    }
    
    // Small delay to ensure cleanup completes
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 100);
  };

  const handleCancelNavigation = () => {
    setShowModal(false);
  };

  // Modal component
  const NavigationWarningModal = showModal ? (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      animation: 'fadeIn 0.2s ease-in'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '4px solid #FFDC00',
        borderRadius: '0',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 0 30px rgba(255, 220, 0, 0.5)',
        animation: 'scaleIn 0.3s ease-out'
      }}>
        <h2 style={{
          fontFamily: "'Bangers', 'Arial Black', sans-serif",
          fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
          color: '#FFDC00',
          textAlign: 'center',
          marginBottom: '20px',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          textShadow: '2px 2px 0px #000, 3px 3px 0px #FF0000'
        }}>
          ⚠️ WARNING!
        </h2>
        
        <p style={{
          fontFamily: "'Passion One', Arial, sans-serif",
          fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
          color: '#FFFFFF',
          textAlign: 'center',
          marginBottom: '25px',
          lineHeight: '1.5'
        }}>
          Using the browser back button will return you to the home page and you will need to restart.
          <br/><br/>
          <strong style={{ color: '#FFDC00' }}>To navigate properly, please use the BACK buttons within the app.</strong>
          <br/><br/>
          Do you want to return to the home page?
        </p>

        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleCancelNavigation}
            style={{
              padding: '12px 30px',
              fontSize: 'clamp(1rem, 2vw, 1.3rem)',
              fontWeight: '900',
              fontFamily: "'Bangers', 'Arial Black', sans-serif",
              color: '#FFFFFF',
              background: 'linear-gradient(135deg, #00CC00 0%, #009900 100%)',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 6px 12px rgba(0, 0, 0, 0.8)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)';
            }}
          >
            ✓ STAY HERE
          </button>

          <button
            onClick={handleConfirmNavigation}
            style={{
              padding: '12px 30px',
              fontSize: 'clamp(1rem, 2vw, 1.3rem)',
              fontWeight: '900',
              fontFamily: "'Bangers', 'Arial Black', sans-serif",
              color: '#FFFFFF',
              background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 6px 12px rgba(0, 0, 0, 0.8)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)';
            }}
          >
            ← GO TO HOME
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  ) : null;

  return { NavigationWarningModal };
};

export default useNavigationWarning;